import {action, makeAutoObservable} from 'mobx'
import {IStores} from './rootStore'
import CheckoutStore from './CheckoutStore'
import {Product, Collection} from 'shopify-storefront-api-typings'
import {cartApiService, CartLineInput} from '../services/cartApi'
import {productApiService} from '../services/productApi'

export interface ExtendedLineItem {
  id: string | number
  quantity: number
  title?: string
  customAttributes?: [
    {
      key: string
      value: string
    },
  ]
  variant?: {
    sku: string
    title?: string
    product?: {
      handle?: string
    }
    price: {
      amount: string
      currencyCode: string
    }
    priceV2: {
      amount: string
      currencyCode: string
    }
    image: {
      src: string
      height: number
      width: number
      altText: string
    }
  }
}

export interface ICollection {
  description?: string
  id?: string | number | undefined
  image: {
    altText: string
    id: string
    src: string
  }
  title: string
  products: Product[]
}

export interface IVariant {
  available?: boolean
  compareAtPrice?: string
  formattedPrice?: string
  grams?: number
  id: string | number
  optionValues?: any[]
  price?: {
    amount: string
    currencyCode: string
  }
  priceV2?: {
    amount: string
    currencyCode: string
  }
  image?: {
    src?: string
    height?: number
    width?: number
    altText?: string
  }
  productId?: string | number
  productTitle?: string
  sku?: string
  title?: string | undefined
}

export interface IImage {
  id: string | number
  created_at?: string
  position?: number
  updated_at?: string
  product_id?: string
  src: string
  variant_ids?: Array<string>
}

export interface IProduct {
  id?: string | number
  title?: string | undefined
  images?: IImage[]
  handle?: string
  collections?: Collection[]
  description?: string
  descriptionHtml?: string
  options?: any[]
  variants: IVariant[]
  selectedVariantImage?: {
    id?: string | number
    created_at?: string
    position?: number
    updated_at?: string
    product_id?: string
    src?: string
    variant_ids?: string[]
  }
  selections?: string[]
  vendor?: string
}

export interface ICart {
  subtotalPrice?: string
  checkoutUrl?: string
  totalPrice?: {
    amount: string
    currencyCode: string
  }
  id?: string | number
  currencyCode?: string
  shippingLine?: number
  discountApplications?: [
    {
      applicable: false
      value: {
        percentage: 0
      }
      code: ''
    },
  ]
  lineItemsSubtotalPrice?: {
    amount: string
    currencyCode: string
  }
  totalPriceV2?: {
    amount: string
    currencyCode: string
  }
  subtotalPriceV2?: {
    amount: string
    currencyCode: string
  }
  totalTaxV2?: {
    amount: string
    currencyCode: string
  }
  lineItems?: ExtendedLineItem[]
}

export default class ProductsStore {
  rootStore: IStores
  checkoutStore: CheckoutStore
  constructor(rootStore: IStores, checkoutStore: CheckoutStore) {
    this.rootStore = rootStore
    this.checkoutStore = checkoutStore
    makeAutoObservable(this)
  }

  public isLoading = false
  public isDiscountCheckLoading = false
  public hasDiscountChecked = false
  public showSearchBar = false
  public noProductsFoundNotice = false
  public productsFilteredNotice = false
  public isProductLoading = {itemId: ''}
  public products!: Product[]
  public productsToDisplay!: Product[]

  public selectedProduct!: Product
  public showProductDetailModal = false
  public selectedVariantId = ''
  public collections!: Collection[]
  public checkoutId = ''
  public cart: ICart = {
    id: '',
    currencyCode: '',
    lineItems: [],
  }
  public discountAdded = false

  @action
  public toggleSearchBar(): void {
    this.showSearchBar = !this.showSearchBar
    this.noProductsFoundNotice = false
  }

  @action
  public setProductDetailModal(depends: boolean): void {
    this.showProductDetailModal = depends
  }

  @action
  public setProducts(products: Product[]) {
    this.isLoading = true

    this.products = products
    this.productsToDisplay = products

    this.isLoading = false
  }

  @action
  public filterProductsBySearchString(searchString: string): void {
    this.isLoading = true

    let productsToDisplay

    if (searchString) {
      const foundProducts = this.products.filter(
        product =>
          (product.variants.edges[0].node.sku &&
            product.variants.edges[0].node.sku
              .toLowerCase()
              .includes(searchString.toLowerCase())) ||
          product.title.toLowerCase().includes(searchString.toLowerCase()),
      )

      if (foundProducts.length !== 0) {
        productsToDisplay = foundProducts
        this.productsFilteredNotice = true
      }
    }

    this.productsToDisplay = productsToDisplay
      ? productsToDisplay
      : this.products

    this.noProductsFoundNotice =
      productsToDisplay || searchString.length === 0 ? false : true

    this.productsFilteredNotice = productsToDisplay ? true : false

    this.isLoading = false
  }

  @action
  public setCollections(collections: any) {
    this.collections = collections
  }

  @action
  public setSelectedProduct(product: any): void {
    this.selectedProduct = product
  }

  @action
  public async fetchSingleProduct(handle: string): Promise<void> {
    try {
      const product = await productApiService.getProductByHandle(handle)
      this.setSelectedProduct(product)
    } catch (error) {
      console.error('Error fetching product:', error)
    }
  }

  @action
  public filterProductsToDisplay(category: string | number): void {
    this.isLoading = true

    const filteredCollection = this.collections.filter(c => c.id === category)

    const productsAfterFilter: Product[] =
      filteredCollection[0].products.edges.map(edge => edge.node)

    if (filteredCollection.length) {
      this.productsToDisplay = productsAfterFilter
    } else {
      this.productsToDisplay = this.products
    }

    this.isLoading = false
  }

  @action
  public async initializeCart(): Promise<void> {
    this.isLoading = true

    const id: string = localStorage.getItem('cartId') || this.checkoutId

    let newCart: ICart
    let newCartId: string
    if (id) {
      try {
        newCart = await cartApiService.getCart(id)
        newCartId = newCart.id as string
      } catch (error) {
        // If cart doesn't exist, create a new one
        newCart = await cartApiService.createCart()
        newCartId = newCart.id as string
        localStorage.setItem('cartId', newCartId)
      }
    } else {
      newCart = await cartApiService.createCart()
      newCartId = newCart.id as string
      localStorage.setItem('cartId', newCartId)
    }

    this.checkoutId = newCartId
    this.cart = newCart

    this.rootStore.checkoutStore.setCheckoutStep('form')

    this.isLoading = false
  }

  @action
  public async addItemToCart(
    quantity: number,
    variantId: string,
    selectedCollectionHandle?: string,
  ): Promise<void> {
    this.isLoading = true
    this.selectedVariantId = variantId

    const id: string = localStorage.getItem('cartId') || this.checkoutId

    let newCart

    if (id) {
      this.checkoutId = id
      const lineInput: CartLineInput = {
        merchandiseId: this.selectedVariantId,
        quantity: quantity,
        attributes: selectedCollectionHandle
          ? [
              {
                key: 'collectionHandle',
                value: selectedCollectionHandle,
              },
            ]
          : undefined,
      }
      newCart = await cartApiService.addToCart(this.checkoutId, [lineInput])
      this.cart = newCart
      this.isLoading = false
    } else {
      await this.initializeCart()
      const lineInput: CartLineInput = {
        merchandiseId: this.selectedVariantId,
        quantity: quantity,
      }
      newCart = await cartApiService.addToCart(this.checkoutId, [lineInput])
      this.rootStore.checkoutStore.setCheckoutStep('form')

      this.cart = newCart
      this.isLoading = false
    }
  }

  @action
  public async updateQuantityInCart(
    lineItemId: string | number,
    quantity: number,
  ): Promise<void> {
    this.isProductLoading = {itemId: lineItemId as string}
    const lineItemsToUpdate = [{id: lineItemId as string, quantity}]
    const updatedCart = await cartApiService.updateCartLine(
      this.checkoutId,
      lineItemsToUpdate,
    )
    this.cart = updatedCart
    this.isProductLoading = {itemId: ''}
  }

  @action
  public async addDiscount(userCodeEntry: string) {
    this.isDiscountCheckLoading = true
    const cartId = this.checkoutId // ID of an existing cart
    const discountCode = userCodeEntry
    this.hasDiscountChecked = true

    // Add a discount code to the cart
    const updatedCart = await cartApiService.applyDiscount(cartId, discountCode)
    this.cart = updatedCart
    this.isDiscountCheckLoading = false
  }

  @action
  public async removeDiscount() {
    this.isDiscountCheckLoading = true

    const cartId = this.checkoutId // ID of an existing cart

    // Removes the applied discount from an existing cart.
    const updatedCart = await cartApiService.applyDiscount(cartId, '')
    this.cart = updatedCart
    this.hasDiscountChecked = false
    this.isDiscountCheckLoading = false
  }

  @action
  public async removeItemInCart(lineItemId: string) {
    this.isProductLoading = {itemId: lineItemId as string}
    const lineItemIdsToRemove = [lineItemId]

    const updatedCart = await cartApiService.removeFromCart(
      this.checkoutId,
      lineItemIdsToRemove,
    )
    this.cart = updatedCart
    this.isProductLoading = {itemId: ''}
  }
}
